import type { AdminInsights, QuestionConfig } from "../../lib/performance-lens/types.ts";
import styles from "./role-scorecards.module.css";

type ItemScore = AdminInsights["items"][number];
type Role = { code: string; label: string; items: QuestionConfig[] };
type Kind = ItemScore["kind"];

function weightedAverage(items: ItemScore[]) {
  const rated = items.filter(item => item.average != null && item.reviews > 0);
  const answers = rated.reduce((total, item) => total + item.reviews, 0);
  return answers ? rated.reduce((total, item) => total + item.average! * item.reviews, 0) / answers : null;
}

function format(value: number | null) {
  return value == null ? "—" : value.toFixed(2);
}

export default function RoleScorecards({ roles, scores }: { roles: Role[]; scores: ItemScore[] }) {
  const byQuestion = new Map(scores.map(item => [`${item.roleCode}:${item.questionKey}:${item.kind}`, item]));

  return <div className={styles.grid}>
    {roles.map(role => {
      const forKind = (kind: Kind) => role.items.flatMap(question => {
        const item = byQuestion.get(`${role.code}:${question.questionKey}:${kind}`);
        return item && (kind !== "PEER" || item.reviewers >= 3) ? [item] : [];
      });
      const peer = weightedAverage(forKind("PEER"));
      const manager = weightedAverage(forKind("MANAGER"));
      const self = weightedAverage(forKind("SELF"));
      const reviewAverage = weightedAverage([...forKind("PEER"), ...forKind("MANAGER")]);
      const ratingCount = [...forKind("PEER"), ...forKind("MANAGER")]
        .reduce((total, item) => total + item.reviews, 0);

      return <article className={styles.card} key={role.code} aria-label={`${role.label} scorecard`}>
        <div className={styles.top}>
          <div><span className={styles.roleCode}>{role.code}</span><h3>{role.label}</h3></div>
          <div className={styles.score} aria-label={`Visible review average ${format(reviewAverage)} out of 5`}>
            <strong>{format(reviewAverage)}</strong><span>/ 5</span>
          </div>
        </div>
        <div className={styles.track} {...(reviewAverage == null ? {} : { role: "meter", "aria-label": `${role.label} visible review average`, "aria-valuemin": 0, "aria-valuemax": 5, "aria-valuenow": reviewAverage, "aria-valuetext": `${format(reviewAverage)} out of 5` })}>
          <span style={{ width: `${reviewAverage == null ? 0 : Math.max(0, Math.min(100, reviewAverage * 20))}%` }} />
        </div>
        <p className={styles.caption}>{ratingCount ? `${ratingCount} visible rated answers · ${role.items.length} items` : `No visible review average · ${role.items.length} items`}</p>
        <div className={styles.breakdown} aria-label={`${role.label} rating sources`}>
          <div><span>Peer</span><strong>{format(peer)}</strong></div>
          <div><span>Manager</span><strong>{format(manager)}</strong></div>
          <div><span>Self</span><strong>{format(self)}</strong></div>
        </div>
        <div className={styles.items}>
          {role.items.map(question => {
            const peerItem = byQuestion.get(`${role.code}:${question.questionKey}:PEER`);
            const managerItem = byQuestion.get(`${role.code}:${question.questionKey}:MANAGER`);
            const selfItem = byQuestion.get(`${role.code}:${question.questionKey}:SELF`);
            return <div className={styles.item} key={question.id}>
              <strong>{question.label}</strong>
              <small>Peer {peerItem && peerItem.reviewers >= 3 ? format(peerItem.average) : "Confidential"} · Manager {format(managerItem?.average ?? null)} · Self {format(selfItem?.average ?? null)}</small>
            </div>;
          })}
          {!role.items.length && <p>No active questions for this role.</p>}
        </div>
      </article>;
    })}
  </div>;
}
